import React from 'react';
import styled from 'styled-components';
import { Type } from '@spotify-internal/encore-web';
import {
  screenXsMax,
  screenSmMax,
  spacer8,
  spacer16,
  spacer24,
  spacer32,
  spacer40,
  spacer56,
  spacer80,
} from '@spotify-internal/encore-foundation/web';
import { DELAYS, withAnimationContext } from '../../../ui';
import { AnimationContextType, fadeInAnimation } from '../../../ui/Animations';

const Testimony = styled.div`
  display: flex;
  flex-direction: column;
  text-align: ${(props: { centered: boolean }) =>
    props.centered ? 'center' : 'left'};
`;

const TestimonialChar = withAnimationContext(
  styled(Type.h1).attrs({
    variant: Type.display2,
    weight: Type.bold,
  })<{ isHero: boolean; animationContext: AnimationContextType }>`
    ${fadeInAnimation(DELAYS.SECOND)};
    line-height: 14px;
    ${(props: { isHero: boolean }) =>
      props.isHero ? `font-size ${spacer80}` : ''};
    padding: 0;
    ${(props: { isHero: boolean }) =>
      props.isHero ? `padding-top: ${spacer56}` : `padding-top: ${spacer24}`};

    @media (max-width: ${screenSmMax}) {
      ${(props: { isHero: boolean }) =>
        props.isHero ? `padding-top: ${spacer56}` : `padding-top: ${spacer16}`};
    }
  `,
);

// TODO: the font-size and line-heights below come directly from
// figma specs.  need to find a better way to do this
const TestimonialBody = withAnimationContext(
  styled(Type.p).attrs({
    variant: Type.heading2,
    weight: Type.book,
  })<{
    centered: boolean;
    isHero: boolean;
    animationContext: AnimationContextType;
  }>`
    ${fadeInAnimation(DELAYS.THIRD)};
    @media (max-width: ${screenSmMax}) {
      ${(props: { centered: boolean; isHero: boolean }) => {
        if (props.isHero) {
          if (props.centered) {
            return `font-size: ${spacer32}; line-height: ${spacer40};`;
          }
          return `font-size: 18px; line-height: ${spacer24};`;
        }
        return `font-size: ${spacer16}; line-height: ${spacer24};`;
      }}
    }

    @media (max-width: ${screenXsMax}) {
      ${(props: { centered: boolean; isHero: boolean }) => {
        if (props.isHero) {
          if (props.centered) {
            return `font-size: ${spacer24}; line-height: ${spacer32};`;
          }
        }
        return '';
      }}
    }
  `,
);

const TestimonialSource = withAnimationContext(
  styled(Type.h1).attrs({
    variant: Type.heading3,
    weight: Type.bold,
  })<{
    centered: boolean;
    isHero: boolean;
    animationContext: AnimationContextType;
  }>`
    ${fadeInAnimation(DELAYS.FOURTH)};
    padding-bottom: 0px;

    @media (max-width: ${screenSmMax}) {
      ${(props: { centered: boolean; isHero: boolean }) =>
        (props.centered && props.isHero) || !props.isHero
          ? ''
          : `visibility: hidden;`};
    }
  `,
);

const SourceTitle = withAnimationContext(
  styled(Type.p).attrs({
    variant: Type.cta3,
  })<{
    centered: boolean;
    isHero: boolean;
    animationContext: AnimationContextType;
  }>`
    ${fadeInAnimation(DELAYS.FIFTH)};
    padding-top: ${spacer8};

    @media (max-width: ${screenSmMax}) {
      ${(props: { centered: boolean; isHero: boolean }) =>
        (props.centered && props.isHero) || !props.isHero
          ? ''
          : `visibility: hidden;`};
    }
  `,
);

export type TestimonialType = {
  quote?: string;
  source?: string;
  jobTitle?: string;
  centered?: boolean;
  isHero?: boolean;
};

export const Testimonial: React.FC<TestimonialType> = ({
  quote = '',
  source = '',
  jobTitle = '',
  centered = false,
  isHero = false,
}) => {
  return (
    <Testimony centered={centered}>
      <TestimonialChar isHero={isHero} className="quote-char">
        “
      </TestimonialChar>
      <TestimonialBody centered={centered} isHero={isHero}>
        {quote}
      </TestimonialBody>
      <TestimonialSource centered={centered} isHero={isHero}>
        {' '}
        &mdash; {source}
      </TestimonialSource>
      <SourceTitle centered={centered} isHero={isHero}>
        {jobTitle}
      </SourceTitle>
    </Testimony>
  );
};
